<script>
//Repositories
import Repositories from '@/repositories';
const WorkOrdersRepository = Repositories.get('workorders');

//Mixins
import statusMixin from '@/mixins/statusMixin';

//Components
import Card from './card.vue';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

//Modals
import ModalLayout from '@/router/layouts/modal-view.vue';
import WorkOrderInfoModal from '../../management/main.vue';

export default {
  props: {
    filters: Object,
    fields: Array,
  },
  components: {
    Card,
    ModalLayout,
    WorkOrderInfoModal,
    Loading,
  },
  mixins: [statusMixin],
  data() {
    return {
      lists: null,
      isLoading: false,
      workOrderForModal: null,
      defaultFilters: { currentPage: 1, perPage: 20 },
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;
      let promisesList = [];
      let tempLists = {};
      for (const statusCode of this.selectedStatus) {
        let promise = WorkOrdersRepository.getAll({
          ...this.defaultFilters,
          statusCodes: [statusCode],
          search: this.filters.search,
        }).then((resp) => {
          tempLists[statusCode] = {
            ...resp.data,
            ...this.defaultFilters,
            isLoading: false,
          };
        });

        promisesList.push(promise);
      }

      Promise.all(promisesList)
        .then(() => {
          //Sorting by key
          this.lists = Object.keys(tempLists)
            .sort((x, y) => {
              let xStatus = this.getWorkOrderStatus().find(
                (z) => z.statusCode == x
              );
              let yStatus = this.getWorkOrderStatus().find(
                (z) => z.statusCode == y
              );

              if (xStatus != null && yStatus != null) {
                return xStatus.statusOrder - yStatus.statusOrder;
              } else {
                return -1;
              }
            })
            .reduce((obj, key) => {
              obj[key] = tempLists[key];
              return obj;
            }, {});
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        })
        .finally(() => (this.isLoading = false));
    },
    loadMoreWorkOrders(statusCode) {
      let column = this.lists[statusCode];

      if (column != null) {
        if (column.count > column.perPage * column.currentPage) {
          this.lists[statusCode] = { ...column, isLoading: true };

          WorkOrdersRepository.getAll({
            ...this.defaultFilters,
            currentPage: column.currentPage + 1,
            statusCodes: [statusCode],
            search: this.filters.search,
          }).then((resp) => {
            this.lists[statusCode] = {
              ...column,
              currentPage: column.currentPage + 1,
              isLoading: false,
              data: [...column.data, ...resp.data.data],
              count: resp.data.count,
            };
          });
        }
      }
    },
    openModal(workOrder, modalName) {
      this.workOrderForModal = workOrder;
      this.lastClickedOrder = modalName + '-' + workOrder.idPublic;
      this.$bvModal.show(modalName);
    },
    modalClosed() {
      this.loadData();
    },
  },
  computed: {
    selectedStatus() {
      if (
        this.filters != null &&
        this.filters.statusCodes != null &&
        this.filters.statusCodes.length > 0
      ) {
        return this.filters.statusCodes;
      }

      return this.getWorkOrderStatus().map((x) => x.statusCode);
    },
  },
  watch: {
    filters() {
      this.lists = null;
      this.loadData();
    },
  },
};
</script>

<template>
  <div class="remaining-space-block customScrollbar">
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>

    <div v-if="lists != null" class="columns">
      <template v-for="(value, key) in lists">
        <div
          v-if="value.data != null && value.data.length > 0"
          class="column custom-inventory-shadow"
          :key="key"
        >
          <div
            class="column-header"
            :class="'bg-' + getStatusFromCode(key, 'WorkOrders').statusVariant"
          >
            {{ $t(getStatusFromCode(key, 'WorkOrders').statusName) }}
          </div>
          <div class="column-content">
            <Card
              v-for="element in value.data"
              :key="element.idPublic"
              :element="element"
              class="single-card"
              @open-modal="openModal(element, 'work-order-info-modal')"
              @work-order-updated="loadData"
            />
            <div v-if="value.count > value.data.length">
              <b-card
                class="single-card"
                style="margin: 5px; margin-bottom: 0; min-height: 70px"
                no-body
              >
                <b-card-body
                  @click="loadMoreWorkOrders(key)"
                  class="clickable-text flex-vertical-align"
                  style="text-align: center"
                >
                  <i
                    v-if="value.isLoading"
                    class="bx bx-loader-alt bx-spin text-primary"
                    style="font-size: 2em"
                  >
                  </i>
                  <span v-else>
                    {{ $t('general.loadMore') }}
                  </span>
                </b-card-body>
              </b-card>
            </div>
          </div>
        </div>
      </template>
    </div>

    <ModalLayout
      name="work-order-info-modal"
      @modal-closed="modalClosed"
      :title="
        $t('workOrders.workOrder') +
        ' ' +
        (workOrderForModal != null ? workOrderForModal.name : '')
      "
      :route="{
        name: 'manageWorkOrder',
        params: {
          idPublic: workOrderForModal != null ? workOrderForModal.idPublic : '',
        },
      }"
    >
      <div v-if="workOrderForModal != null">
        <WorkOrderInfoModal :workOrderIdPublic="workOrderForModal.idPublic" />
      </div>
    </ModalLayout>
  </div>
</template>

<style scoped>
.remaining-space-block {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
}
.columns {
  position: relative;
  flex-flow: row nowrap;
  flex: 1 1 1px;
  min-height: 0px;
  overflow-x: auto;
  display: flex;
  padding: 0 0 10px 0;
  background-color: transparent;
}
.column {
  background-color: #f1f1f1;
  margin-left: 5px;
  margin-right: 5px;
  min-width: 350px;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
}
.column-content {
  overflow-y: auto;
}
.column-header {
  padding: 15px;
  border-radius: 5px 5px 0 0;
  color: white;
  font-size: 1.3em;
  font-weight: bold;
}
.column:first-child {
  margin-left: 0px;
}
.column:last-child {
  margin-right: 0px;
}

.single-card:last-child {
  margin-bottom: 5px !important;
}
</style>